import React from "react";
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  Button,
  Grid,
  HStack,
  VStack,
  FormControl,
  FormLabel,
  Select,
  Input,
  Textarea,
  Text,
  Box,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";

import { InvoiceForm } from "api/invoice";
import { CustomerIdNameAddressPlant } from "api/customer";

const PURPLE = "#7062B9";
const PURPLE_DARK = "#5B4FA3";
const PURPLE_DARKER = "#4E4390";
const NAVY = "#2D2A5E";
const primaryBtn = {
  bg: PURPLE,
  color: "white",
  _hover: { bg: PURPLE_DARK },
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  isEdit: boolean;
  form: InvoiceForm;
  customers: CustomerIdNameAddressPlant[];
  typeOptions: string[];
  referenceOptions: string[];
  subjectOptions: string[];

  handleChange: (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => void;
  handleQuotationFieldChange: (
    field: "reference" | "subject",
    value: string
  ) => void;

  handleCustomerChange: (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => void;

  handleSubmit: () => void;
  handleReset: () => void;
}

const AddInvoiceDrawer = ({
  isOpen,
  onClose,
  isEdit,
  form,
  customers,
  typeOptions,
  referenceOptions,
  subjectOptions,
  handleChange,
  handleQuotationFieldChange,
  handleCustomerChange,
  handleSubmit,
  handleReset,
}: Props) => {
  const [customReference, setCustomReference] = React.useState("");
  const [customSubject, setCustomSubject] = React.useState("");
  const [showCustomReference, setShowCustomReference] = React.useState(false);
  const [showCustomSubject, setShowCustomSubject] = React.useState(false);
  const [isReferenceOpen, setIsReferenceOpen] = React.useState(false);
  const [isSubjectOpen, setIsSubjectOpen] = React.useState(false);

  React.useEffect(() => {
    if (!isOpen) {
      setCustomReference("");
      setCustomSubject("");
      setShowCustomReference(false);
      setShowCustomSubject(false);
      setIsReferenceOpen(false);
      setIsSubjectOpen(false);
    }
  }, [isOpen]);

  const addCustomValue = (field: "reference" | "subject") => {
    const value = field === "reference" ? customReference.trim() : customSubject.trim();
    if (!value) return;
    handleQuotationFieldChange(field, value);
    if (field === "reference") {
      setCustomReference("");
      setShowCustomReference(false);
    } else {
      setCustomSubject("");
      setShowCustomSubject(false);
    }
  };
  /* ============================================================
     COMMON FIELD STYLE
  ============================================================ */

  const fieldStyle = {
    width: "100%",
    height: "56px",
    minHeight: "56px",
    maxWidth: "100%",

    boxSizing: "border-box" as const,

    border: "1px solid #D5DCE8",
    borderRadius: "14px",

    fontSize: "16px",
    color: NAVY,
    background: "white",

    margin: "0",

    _hover: {
      borderColor: "#B8C2D1",
    },

    _focus: {
      borderColor: PURPLE,
      boxShadow: `0 0 0 1px ${PURPLE}`,
    },
  };

  const labelStyle = {
    fontSize: "sm",
    fontWeight: "700",
    color: NAVY,
    marginBottom: "8px",
  };

  return (
    <Drawer
      isOpen={isOpen}
      placement="right"
      onClose={onClose}
      size="xl"
      blockScrollOnMount={true}
      preserveScrollBarGap={true}
    >
      <DrawerOverlay bg="rgba(0, 0, 0, 0.35)" />

      <DrawerContent
        h="100vh"
        maxH="100vh"
        minH={0}
        display="flex"
        flexDirection="column"
        bg="white"
        borderTopLeftRadius="24px"
        borderBottomLeftRadius="24px"
        overflow="hidden"
      >
        {/* =====================================================
            HEADER
        ====================================================== */}

        <DrawerHeader
          flexShrink={0}
          bg={`linear-gradient(135deg, ${PURPLE_DARK} 0%, ${PURPLE} 100%)`}
          color="white"
          px={6}
          py={7}
          minH="125px"
          position="relative"
        >
          <Text
            fontSize="30px"
            fontWeight="700"
            lineHeight="1.2"
            mb={2}
          >
            {isEdit ? "Update Quotation" : "Add Quotation"}
          </Text>

          <Text
            fontSize="16px"
            fontWeight="400"
            opacity={0.95}
          >
            Enter service / visit information
          </Text>

          <DrawerCloseButton
            position="absolute"
            right="18px"
            top="22px"
            size="lg"
            color="white"
            fontSize="18px"
            _hover={{
              bg: "rgba(255,255,255,0.15)",
            }}
          />
        </DrawerHeader>

        {/* =====================================================
            BODY
        ====================================================== */}

        <DrawerBody
          py={6}
          flex="1"
          minH={0}
          maxH="100%"
          overflowY="auto"
          overflowX="hidden"
          overscrollBehavior="contain"
          css={{
            "&::-webkit-scrollbar": {
              width: "7px",
            },

            "&::-webkit-scrollbar-track": {
              background: "#f1f1f1",
            },

            "&::-webkit-scrollbar-thumb": {
              background: "#b8b8b8",
              borderRadius: "10px",
            },
          }}
        >
          <Grid
            width="100%"
            templateColumns={{ base: "minmax(0, 1fr)", md: "repeat(2, minmax(0, 1fr))" }}
            gap={5}
          >
            {/* =================================================
                CUSTOMER
            ================================================== */}

            <FormControl
              width="100%"
              maxWidth="100%"
              minWidth="0"
              m="0"
            >
              <FormLabel {...labelStyle}>
                Customer
              </FormLabel>

              {/* Select wrapper */}
              <Box
                position="relative"
                width="100%"
                maxWidth="100%"
                minWidth="0"
                m="0"
                p="0"
              >
                <Select
                  id="customerId"
                  variant="outline"
                  placeholder="Select Customer"
                  value={form.customerId || ""}
                  onChange={handleCustomerChange}
                  width="100%"
                  maxWidth="100%"
                  minWidth="0"
                  height="56px"
                  minHeight="56px"
                  margin="0"
                  paddingLeft="20px"
                  paddingRight="50px"
                  borderRadius="14px"
                  border="1px solid #D5DCE8"
                  fontSize="16px"
                  color={NAVY}
                  bg="white"
                  boxSizing="border-box"
                  icon={<ChevronDownIcon />}
                  iconColor={NAVY}
                  _hover={{
                    borderColor: "#B8C2D1",
                  }}
                  _focus={{
                    borderColor: PURPLE,
                    boxShadow: `0 0 0 1px ${PURPLE}`,
                  }}
                >
                  {customers.map((customer) => (
                    <option
                      key={customer.id}
                      value={customer.id}
                    >
                      {customer.customerName}
                    </option>
                  ))}
                </Select>

              </Box>
            </FormControl>

            {/* =================================================
                TYPE
            ================================================== */}

            <FormControl
              width="100%"
              maxWidth="100%"
              minWidth="0"
              m="0"
            >
              <FormLabel {...labelStyle}>
                Type
              </FormLabel>

              {/* Select wrapper */}
              <Box
                position="relative"
                width="100%"
                maxWidth="100%"
                minWidth="0"
                m="0"
                p="0"
              >
                <Select
                  id="type"
                  variant="outline"
                  value={form.type}
                  onChange={handleChange}
                  width="100%"
                  maxWidth="100%"
                  minWidth="0"
                  height="56px"
                  minHeight="56px"
                  margin="0"
                  paddingLeft="20px"
                  paddingRight="50px"
                  borderRadius="14px"
                  border="1px solid #D5DCE8"
                  fontSize="16px"
                  color={NAVY}
                  bg="white"
                  boxSizing="border-box"
                  icon={<ChevronDownIcon />}
                  iconColor={NAVY}
                  _hover={{
                    borderColor: "#B8C2D1",
                  }}
                  _focus={{
                    borderColor: PURPLE,
                    boxShadow: `0 0 0 1px ${PURPLE}`,
                  }}
                >
                  <option value="" disabled>
                    Select Type
                  </option>
                  {typeOptions.map((option) => (
                    <option
                      key={option}
                      value={option}
                    >
                      {option}
                    </option>
                  ))}
                </Select>

              </Box>
            </FormControl>

            {/* =================================================
                LOCATION
            ================================================== */}

            <FormControl
              width="100%"
              maxWidth="100%"
              minWidth="0"
              m="0"
            >
              <FormLabel {...labelStyle}>
                Location
              </FormLabel>

              <Input
                id="location"
                value={form.location || ""}
                onChange={handleChange}
                placeholder="Location will appear automatically"
                isReadOnly
                {...fieldStyle}
                px="20px"
              />
            </FormControl>

            {/* =================================================
                EQUIPMENT NAME
            ================================================== */}

            <FormControl
              width="100%"
              maxWidth="100%"
              minWidth="0"
              m="0"
            >
              <FormLabel {...labelStyle}>
                Equipment Name
              </FormLabel>

              <Input
                id="equipmentName"
                value={form.equipmentName || ""}
                onChange={handleChange}
                placeholder="Enter equipment name"
                {...fieldStyle}
                px="20px"
              />
            </FormControl>

            {/* =================================================
                NO. OF VISITS
            ================================================== */}

            <FormControl
              width="100%"
              maxWidth="100%"
              minWidth="0"
              m="0"
            >
              <FormLabel {...labelStyle}>
                No. of Visits
              </FormLabel>

              <Input
                id="noOfVisit"
                type="number"
                min={0}
                value={
                  form.noOfVisit === undefined ||
                  form.noOfVisit === null
                    ? ""
                    : form.noOfVisit
                }
                onChange={handleChange}
                placeholder="Enter number of visits"
                {...fieldStyle}
                px="20px"
              />
            </FormControl>

            {/* =================================================
                PLANT
            ================================================== */}

            <FormControl
              width="100%"
              maxWidth="100%"
              minWidth="0"
              m="0"
            >
              <FormLabel {...labelStyle}>
                Plant
              </FormLabel>

              <Input
                id="plant"
                value={form.plant || ""}
                onChange={handleChange}
                placeholder="Plant will appear automatically"
                isReadOnly
                {...fieldStyle}
                px="20px"
              />
            </FormControl>

            {/* =================================================
                SERVICE AMOUNT
            ================================================== */}

            <FormControl
              width="100%"
              maxWidth="100%"
              minWidth="0"
              m="0"
            >
              <FormLabel {...labelStyle}>
                Service Amount
              </FormLabel>

              <Input
                id="serviceAmount"
                type="number"
                min={0}
                step="0.01"
                value={
                  form.serviceAmount === undefined ||
                  form.serviceAmount === null
                    ? ""
                    : form.serviceAmount
                }
                onChange={handleChange}
                onWheel={(event) => event.currentTarget.blur()}
                placeholder="Enter service amount"
                {...fieldStyle}
                px="20px"

              />
            </FormControl>

            <FormControl
              width="100%"
              maxWidth="100%"
              minWidth="0"
              m="0"
            >
              <FormLabel {...labelStyle}>
                Support Amount
              </FormLabel>

              <Input
                id="supportAmount"
                type="number"
                min={0}
                step="0.01"
                value={
                  form.supportAmount === undefined ||
                  form.supportAmount === null
                    ? ""
                    : form.supportAmount
                }
                onChange={handleChange}
                onWheel={(event) => event.currentTarget.blur()}
                placeholder="Enter support amount"
                {...fieldStyle}
                px="20px"

              />
            </FormControl>

            <FormControl
              width="100%"
              maxWidth="100%"
              minWidth="0"
              m="0"
              order={3}
            >
              <FormLabel {...labelStyle}>Kindly Attention</FormLabel>
              <Textarea
                id="kindlyAttend"
                value={form.kindlyAttend || ""}
                onChange={handleChange}
                placeholder="Enter kindly attend details"
                minHeight="110px"
                {...fieldStyle}
                px="20px"
                resize="vertical"
              />
            </FormControl>

            <FormControl
              width="100%"
              maxWidth="100%"
              minWidth="0"
              m="0"
              order={2}
            >
              <FormLabel {...labelStyle}>Reference</FormLabel>
              <Button
                type="button"
                variant="outline"
                width="100%"
                justifyContent="space-between"
                fontWeight="normal"
                textAlign="left"
                rightIcon={
                  <ChevronDownIcon />
                }
                aria-expanded={isReferenceOpen}
                onClick={() => setIsReferenceOpen((open) => !open)}
                {...fieldStyle}
                px="20px"
                _hover={{ borderColor: "#B8C2D1", bg: "white" }}
                _active={{ bg: "white" }}
                _focus={{ borderColor: "#D5DCE8", boxShadow: "none" }}
                _focusVisible={{ borderColor: "#D5DCE8", boxShadow: "none" }}
              >
                <Text noOfLines={1} color={form.reference ? NAVY : "#718096"}>
                  {form.reference || "Select reference"}
                </Text>
              </Button>
              {isReferenceOpen && (
                <VStack
                  align="stretch"
                  spacing={0}
                  mt={2}
                  maxH="180px"
                  overflowY="auto"
                  border="1px solid #D5DCE8"
                  borderRadius="12px"
                  bg="white"
                  p={1}
                >
                  {Array.from(new Set([...referenceOptions, ...(form.reference ? [form.reference] : [])])).map((option) => (
                    <Button
                      key={option}
                      type="button"
                      variant="ghost"
                      justifyContent="flex-start"
                      fontWeight="normal"
                      color={NAVY}
                      _hover={{ bg: "#F7F8FA" }}
                      _focus={{ boxShadow: "none", bg: "#F7F8FA" }}
                      onClick={() => {
                        handleQuotationFieldChange("reference", option);
                        setIsReferenceOpen(false);
                        setShowCustomReference(false);
                      }}
                    >
                      {option}
                    </Button>
                  ))}
                  <Button
                    type="button"
                    variant="ghost"
                    justifyContent="flex-start"
                    fontWeight="600"
                    color={PURPLE}
                    _hover={{ bg: "#F7F8FA" }}
                    _focus={{ boxShadow: "none", bg: "#F7F8FA" }}
                    onClick={() => {
                      setShowCustomReference(true);
                      setIsReferenceOpen(false);
                    }}
                  >
                    + Add Custom Reference
                  </Button>
                </VStack>
              )}
              {showCustomReference && (
                <HStack mt={2}>
                  <Input
                    value={customReference}
                    onChange={(event) => setCustomReference(event.target.value)}
                    placeholder="Enter custom reference"
                    {...fieldStyle}
                    px="20px"
                  />
                  <Button
                    {...primaryBtn}
                    onClick={() => addCustomValue("reference")}
                    isDisabled={!customReference.trim()}
                  >
                    Add
                  </Button>
                </HStack>
              )}
            </FormControl>

            <FormControl
              width="100%"
              maxWidth="100%"
              minWidth="0"
              m="0"
              order={1}
            >
              <FormLabel {...labelStyle}>Subject</FormLabel>
              <Button
                type="button"
                variant="outline"
                width="100%"
                justifyContent="space-between"
                fontWeight="normal"
                textAlign="left"
                rightIcon={
                  <ChevronDownIcon />
                }
                aria-expanded={isSubjectOpen}
                onClick={() => setIsSubjectOpen((open) => !open)}
                {...fieldStyle}
                px="20px"
                _hover={{ borderColor: "#B8C2D1", bg: "white" }}
                _active={{ bg: "white" }}
                _focus={{ borderColor: "#D5DCE8", boxShadow: "none" }}
                _focusVisible={{ borderColor: "#D5DCE8", boxShadow: "none" }}
              >
                <Text noOfLines={1} color={form.subject ? NAVY : "#718096"}>
                  {form.subject || "Select subject"}
                </Text>
              </Button>
              {isSubjectOpen && (
                <VStack
                  align="stretch"
                  spacing={0}
                  mt={2}
                  maxH="180px"
                  overflowY="auto"
                  border="1px solid #D5DCE8"
                  borderRadius="12px"
                  bg="white"
                  p={1}
                >
                  {Array.from(new Set([...subjectOptions, ...(form.subject ? [form.subject] : [])])).map((option) => (
                    <Button
                      key={option}
                      type="button"
                      variant="ghost"
                      justifyContent="flex-start"
                      fontWeight="normal"
                      color={NAVY}
                      _hover={{ bg: "#F7F8FA" }}
                      _focus={{ boxShadow: "none", bg: "#F7F8FA" }}
                      onClick={() => {
                        handleQuotationFieldChange("subject", option);
                        setIsSubjectOpen(false);
                        setShowCustomSubject(false);
                      }}
                    >
                      {option}
                    </Button>
                  ))}
                  <Button
                    type="button"
                    variant="ghost"
                    justifyContent="flex-start"
                    fontWeight="600"
                    color={PURPLE}
                    _hover={{ bg: "#F7F8FA" }}
                    _focus={{ boxShadow: "none", bg: "#F7F8FA" }}
                    onClick={() => {
                      setShowCustomSubject(true);
                      setIsSubjectOpen(false);
                    }}
                  >
                    + Add Custom Subject
                  </Button>
                </VStack>
              )}
              {showCustomSubject && (
                <HStack mt={2}>
                  <Input
                    value={customSubject}
                    onChange={(event) => setCustomSubject(event.target.value)}
                    placeholder="Enter custom subject"
                    {...fieldStyle}
                    px="20px"
                  />
                  <Button
                    {...primaryBtn}
                    onClick={() => addCustomValue("subject")}
                    isDisabled={!customSubject.trim()}
                  >
                    Add
                  </Button>
                </HStack>
              )}
            </FormControl>
          </Grid>
        </DrawerBody>

        {/* =====================================================
            FOOTER
        ====================================================== */}

        <DrawerFooter
          flexShrink={0}
          bg="white"
          borderTop="1px solid"
          borderColor="#E5E7EB"
          px={6}
          py={5}
          gap={4}
          justifyContent="flex-end"
        >
          <Button
            variant="outline"
            size="lg"
            h="50px"
            px={6}
            borderRadius="12px"
            fontSize="16px"
            fontWeight="600"
            borderColor="#D5DCE8"
            color={NAVY}
            onClick={handleReset}
            _hover={{
              bg: "#F7F8FA",
            }}
          >
            Reset
          </Button>

          <Button
            size="lg"
            h="50px"
            px={7}
            borderRadius="12px"
            fontSize="16px"
            fontWeight="700"
            color="white"
            bg={PURPLE}
            onClick={handleSubmit}
            _hover={{
              bg: PURPLE_DARK,
            }}
            _active={{
              bg: PURPLE_DARKER,
            }}
          >
            {isEdit ? "Update Quotation" : "Save Quotation"}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default AddInvoiceDrawer;